import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { rupees, fmtDate } from "@/lib/helpers";
import { StatusBadge, EmptyState } from "@/components/Common";
import { PayCard } from "@/components/PayCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SignOut, Receipt, Coins, BookOpen } from "@phosphor-icons/react";

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/portal/passbook").then(({ data }) => setD(data)).catch(() => {}); }, []);

  const viewSigned = async (enrollId) => {
    const token = localStorage.getItem("og_token");
    const res = await fetch(`${API}/schemes/enrollments/${enrollId}/signed-acceptance`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) window.open(URL.createObjectURL(await res.blob()), "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="h-16 bg-white/95 backdrop-blur border-b border-[#E6E2D6] flex items-center justify-between px-6 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="font-display text-2xl font-semibold text-[#C5A059] tracking-wide">OM GOLD</div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">Customer Passbook</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-[#4A4A4A]">{user?.name}</div>
          <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="portal-logout"><SignOut size={18} /></Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 fade-in">
        {!d ? <div className="text-[#8A8A8A] py-12 text-center">Loading your passbook…</div> : (
          <>
            <Card className="p-6 bg-white border-[#E6E2D6] shadow-sm mb-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-[#C5A059] text-white flex items-center justify-center text-2xl font-medium">{d.customer.name.charAt(0)}</div>
                <div className="flex-1">
                  <div className="font-display text-2xl font-semibold text-[#1A1A1A]">{d.customer.name}</div>
                  <div className="text-sm text-[#8A8A8A]">{d.customer.mobile}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">Total Purchases</div>
                  <div className="text-xl font-medium tabular-nums text-[#8A6D2F]">{rupees(d.total_spent)}</div>
                </div>
              </div>
            </Card>

            <PayCard />

            <Tabs defaultValue="schemes">
              <TabsList className="bg-[#F2EBE1]">
                <TabsTrigger value="schemes" data-testid="portal-tab-schemes"><Coins size={15} className="mr-1" /> My Schemes</TabsTrigger>
                <TabsTrigger value="purchases" data-testid="portal-tab-purchases"><Receipt size={15} className="mr-1" /> Purchases</TabsTrigger>
                <TabsTrigger value="ledger" data-testid="portal-tab-ledger"><BookOpen size={15} className="mr-1" /> Ledger</TabsTrigger>
              </TabsList>

              <TabsContent value="schemes">
                <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
                  {d.enrollments.length === 0 ? <EmptyState label="No schemes yet" /> : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Account</TableHead><TableHead>Scheme</TableHead><TableHead>Progress</TableHead><TableHead className="text-right">Paid</TableHead><TableHead>Status</TableHead><TableHead>Document</TableHead></TableRow></TableHeader>
                      <TableBody>{d.enrollments.map((e) => (
                        <TableRow key={e.id}><TableCell className="font-medium">{e.account_no}</TableCell><TableCell className="text-sm">{e.scheme_name}</TableCell><TableCell className="text-sm">{e.paid_count}/{e.payable_installments || e.duration_months}</TableCell><TableCell className="text-right tabular-nums">{rupees(e.total_paid)}</TableCell><TableCell><StatusBadge status={e.status} />{e.redeem_balance > 0 && <div className="text-[10px] text-[#2D6A4F] mt-0.5">Redeem bal {rupees(e.redeem_balance)}</div>}</TableCell><TableCell>{e.signed_acceptance ? <Button size="sm" variant="ghost" className="h-7 text-[#8A6D2F]" onClick={() => viewSigned(e.id)} data-testid={`portal-signed-${e.account_no}`}>View letter</Button> : <span className="text-xs text-[#8A8A8A]">-</span>}</TableCell></TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="purchases">
                <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
                  {d.sales.length === 0 ? <EmptyState label="No purchases yet" /> : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                      <TableBody>{d.sales.map((s) => (
                        <TableRow key={s.id}><TableCell className="font-medium">{s.invoice_no}</TableCell><TableCell className="text-sm">{fmtDate(s.created_at)}</TableCell><TableCell className="text-right tabular-nums">{rupees(s.grand_total)}</TableCell><TableCell><StatusBadge status={s.status} /></TableCell></TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="ledger">
                <Card className="bg-white border-[#E6E2D6] shadow-sm mt-3">
                  {d.ledger.length === 0 ? <EmptyState label="No ledger entries" /> : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                      <TableBody>{d.ledger.map((l) => (
                        <TableRow key={l.id}><TableCell className="text-sm">{fmtDate(l.at)}</TableCell><TableCell className="capitalize text-sm">{l.type.replace(/_/g, " ")}</TableCell><TableCell className="text-sm">{l.invoice_no || "-"}</TableCell><TableCell className={`text-right tabular-nums ${l.amount < 0 ? "text-[#2D6A4F]" : "text-[#1A1A1A]"}`}>{rupees(l.amount)}</TableCell></TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
        <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-[#8A8A8A]">
          <img src="/microcore-logo.png" alt="MICROCORE" className="h-6 w-6 object-contain" />
          Developed by <a href="https://www.microcoreitsolutions.com/" target="_blank" rel="noopener noreferrer" className="text-[#C5A059] font-semibold hover:underline">MICROCORE IT SOLUTIONS</a>
        </div>
      </main>
    </div>
  );
}
